import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { UserCheck } from 'lucide-react';
import { ROLE_LABELS } from '../../data/mockData';

export function SuccessCredentialsDialog({
  generatedCredentials,
  finishCredentialsView,
}) {
  return (
    <Dialog open={!!generatedCredentials} onOpenChange={() => {}}>
      <DialogContent className="bg-white border-slate-250 max-w-md text-center p-6 rounded-xl shadow-xl">
        <DialogHeader>
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-2">
            <UserCheck className="w-6 h-6" />
          </div>
          <DialogTitle className="text-slate-900 text-lg font-bold">User Created & Hierarchy Mapped!</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Employee dossier initialized. Credentials generated and dispatched automatically via Email & SMS.
          </DialogDescription>
        </DialogHeader>
        {generatedCredentials && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-left space-y-3 my-4">
            <div className="flex justify-between">
              <span className="text-slate-400">Employee ID:</span>
              <strong className="text-slate-800 font-mono">{generatedCredentials.empId}</strong>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">System Username:</span>
              <strong className="text-slate-800 font-mono">{generatedCredentials.username}</strong>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Temporary Password:</span>
              <strong className="text-blue-600 font-mono">{generatedCredentials.tempPassword}</strong>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-2 text-[10px]">
              <span className="text-slate-400">Notifications Sent:</span>
              <span className="text-emerald-600 font-bold uppercase">SUCCESSFUL (Email & SMS)</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white w-full py-5" onClick={finishCredentialsView}>
            Finish and View in Table
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditDossierDialog({
  showEditDialog,
  setShowEditDialog,
  editingUser,
  setEditingUser,
  handleSaveEdit,
}) {
  return (
    <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
      <DialogContent className="bg-white border-slate-250 max-w-lg p-5">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Edit User Dossier Attributes</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Modify biographical and region definitions for this active employee card.
          </DialogDescription>
        </DialogHeader>
        {editingUser && (
          <form onSubmit={handleSaveEdit} className="space-y-4 py-3 text-xs">
            <div className="space-y-1.5">
              <Label className="text-slate-600 font-semibold">Full Name / Corporate Entity Name</Label>
              <Input
                className="bg-slate-50/50 border-slate-200 h-9"
                value={editingUser.name}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 font-semibold">Email ID</Label>
              <Input
                type="email"
                className="bg-slate-50/50 border-slate-200 h-9"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-600 font-semibold">Contact Phone</Label>
                <Input
                  className="bg-slate-50/50 border-slate-200 h-9"
                  value={editingUser.phone}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 font-semibold">Assigned Region</Label>
                <Select value={editingUser.region} onValueChange={(val) => setEditingUser({ ...editingUser, region: val })}>
                  <SelectTrigger className="bg-slate-50/50 border-slate-200 h-9">
                    <SelectValue placeholder="Region" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="North">North Region</SelectItem>
                    <SelectItem value="West">West Region</SelectItem>
                    <SelectItem value="South">South Region</SelectItem>
                    <SelectItem value="East">East Region</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button variant="ghost" type="button" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" type="submit">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ChangeRoleDialog({
  showRoleDialog,
  setShowRoleDialog,
  changeRoleUser,
  newSelectedRole,
  setNewSelectedRole,
  handleSaveRoleChange,
}) {
  return (
    <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
      <DialogContent className="bg-white border-slate-250 max-w-sm p-5">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Change Hierarchy Role</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Re-assign the core operations capability role for this employee card.
          </DialogDescription>
        </DialogHeader>
        {changeRoleUser && (
          <div className="space-y-4 py-2 text-xs">
            <p className="text-slate-600">Re-assigning role for: <strong className="text-slate-800">{changeRoleUser.name}</strong></p>
            <div className="space-y-1.5">
              <Label className="text-slate-500 font-semibold">Target Operational Role</Label>
              <Select value={newSelectedRole} onValueChange={setNewSelectedRole}>
                <SelectTrigger className="bg-slate-50/50 border-slate-200 h-9">
                  <SelectValue placeholder="Role Selection" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {Object.entries(ROLE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-3">
              <Button variant="ghost" onClick={() => setShowRoleDialog(false)}>
                Cancel
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSaveRoleChange}>
                Update Role
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
